import React, { useState, useEffect } from 'react'

import { postDMXCommand } from '../../api'
import { DMX_ACTIONS } from 'utils'

import styles from './DMXDashboard.module.css'

// Example DMX channel mapping from a 12-channel Mini Moving Head Wash Light
// 1 - pan
// 2 - finePan
// 3 - tilt
// 4 - fineTilt
// 5 - dimmer
// 6 - red
// 7 - green
// 8 - blue
// 9 - white
// 10 - color mixed
// 11 - strobe
// 12 auto movement and light

const DMXDashboard = () => {
  const [channels, setChannels] = useState({})
  const [activeChannels, setActiveChannels] = useState([])
  const [newChannel, setNewChannel] = useState('')
  const [status, setStatus] = useState('Ready')
  const [initialized, setInitialized] = useState(false)

  const sendCommand = async (action, params) => {
    try {
      setStatus('Sending...')
      const data = await postDMXCommand(action, params)
      setStatus(data.success ? 'Success' : 'Error')

      setTimeout(() => setStatus('Ready'), 2000)
      return data
    } catch (error) {
      setStatus('Error: ' + error.message)
      return { success: false, error: error.message }
    }
  }

  useEffect(() => {
    const loadConfig = async () => {
      const result = await sendCommand(DMX_ACTIONS.GET_CONFIG, {})
      if (result.success && result.config) {
        setInitialized(result.config.initialized)
        loadChannelValues()
      }
    }

    loadConfig()
  }, [])

  const loadChannelValues = async () => {
    const result = await sendCommand(DMX_ACTIONS.GET_CHANNELS, {})
    if (result.success && result.channels) {
      const channelsObj = {}
      const activeChannelsList = []

      result.channels.forEach((value, index) => {
        const channelNum = index + 1
        channelsObj[channelNum] = value

        if (value > 0) {
          activeChannelsList.push(channelNum.toString())
        }
      })

      setChannels(channelsObj)

      if (activeChannelsList.length === 0) {
        setActiveChannels(['1', '2', '3'])
      } else {
        setActiveChannels(activeChannelsList)
      }
    }
  }

  const handleChannelChange = (channel, value) => {
    const channelNum = parseInt(channel, 10)
    const newValue = parseInt(value, 10)

    const newChannels = { ...channels, [channelNum]: newValue }
    setChannels(newChannels)
    sendCommand(DMX_ACTIONS.SET_CHANNEL, { channel: channelNum, value: newValue })
  }

  const addChannel = () => {
    if (newChannel && !activeChannels.includes(newChannel)) {
      setActiveChannels([...activeChannels, newChannel])
      setNewChannel('')
    }
  }

  const removeChannel = channel => {
    setActiveChannels(activeChannels.filter(ch => ch !== channel))
  }

  const setAllChannels = value => {
    const updates = {}
    activeChannels.forEach(channel => {
      updates[channel] = value
    })

    setChannels({ ...channels, ...updates })
    sendCommand(DMX_ACTIONS.SET_CHANNELS, { channels: updates })
  }

  return (
    <div className={styles.dashboard}>
      <h1 className={styles.title}>DMX Channel Tester</h1>

      <div className={styles.statusBar}>
        <div className={styles.statusIndicator}>
          <div
            className={`${styles.statusDot} ${
              status === 'Ready'
                ? styles.statusReady
                : status.includes('Error')
                ? styles.statusError
                : styles.statusBusy
            }`}
          />
          <span>{status}</span>
        </div>
      </div>

      <div className={styles.section}>
        <h2>Channel Configuration</h2>
        <div className={styles.addChannelForm}>
          <input
            type="number"
            min="1"
            max="512"
            placeholder="Channel #"
            value={newChannel}
            onChange={e => setNewChannel(e.target.value)}
            className={styles.channelInput}
          />
          <button onClick={addChannel} className={styles.addButton}>
            Add Channel
          </button>
        </div>

        <div className={styles.presetsBar}>
          <button onClick={() => setActiveChannels(['1', '2', '3'])}>RGB (1-3)</button>
          <button onClick={() => setActiveChannels(['4', '5', '6'])}>RGB (4-6)</button>
          <button onClick={() => setActiveChannels(['1', '2', '3', '4', '5', '6'])}>
            6-Channel
          </button>
          <button
            onClick={() =>
              setActiveChannels(Array.from({ length: 12 }, (_, i) => (i + 1).toString()))
            }
          >
            12-Channel
          </button>
        </div>
      </div>

      <div className={styles.section}>
        <h2>Channel Controls</h2>
        <div className={styles.channelList}>
          {activeChannels
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(channel => (
              <div key={channel} className={styles.channelControl}>
                <div className={styles.channelHeader}>
                  <label>Channel {channel}</label>
                  <button onClick={() => removeChannel(channel)} className={styles.removeButton}>
                    ×
                  </button>
                </div>

                <div className={styles.channelSlider}>
                  <input
                    type="range"
                    min="0"
                    max="255"
                    value={channels[channel] || 0}
                    onChange={e => handleChannelChange(channel, e.target.value)}
                  />
                  <span className={styles.channelValue}>{channels[channel] || 0}</span>
                </div>
              </div>
            ))}
        </div>

        {activeChannels.length === 0 && (
          <p className={styles.noChannels}>No channels added. Add some channels to control them.</p>
        )}
      </div>

      <div className={styles.section}>
        <h2>Channel Actions</h2>
        <div className={styles.actionGrid}>
          <button onClick={() => setAllChannels(0)} className={styles.actionButton}>
            All Off
          </button>
          <button onClick={() => setAllChannels(255)} className={styles.actionButton}>
            All On
          </button>
          <button onClick={() => setAllChannels(127)} className={styles.actionButton}>
            All 50%
          </button>
          <button onClick={loadChannelValues} className={styles.actionButton}>
            Refresh
          </button>
          <button
            onClick={() => sendCommand(DMX_ACTIONS.BLACKOUT, {})}
            className={styles.blackoutButton}
          >
            Blackout All
          </button>
        </div>
      </div>
    </div>
  )
}

export default DMXDashboard
